import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiService } from "../../../utils/api";

function AudioDocumentView({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [image, setImage] = useState("");
  const [minutes, setMinutes] = useState("");
  const [editedMinutes, setEditedMinutes] = useState("");
  const [editedTitle, setEditedTitle] = useState("");
  const [editedContent, setEditedContent] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [totalword, setTotalWord] = useState(0);
  // Add new state for find and replace
  const [showFindReplace, setShowFindReplace] = useState(false);
  const [findText, setFindText] = useState("");
  const [replaceText, setReplaceText] = useState("");
  const [findCount, setFindCount] = useState(0);
  // Add state for highlighted content
  const [highlightedContent, setHighlightedContent] = useState("");
  const textareaRef = useRef(null);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [matches, setMatches] = useState([]);
  const replacePendingRef = useRef(false);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await apiService.getAudioDocument(id);
        const data = response.data;

        if (data.success) {
          setDocument(data.document);
          setTitle(data.document.title);
          setContent(data.document.content);
          setImage(data.document.originalFilename);
          setMinutes(data.document.timeTaken);
          setEditedMinutes(data.document.timeTaken.split(":")[0]);
          setEditedTitle(data.document.title);
          setEditedContent(data.document.content);

          setTotalWord(
            data.document.content
              .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()']/g, "")
              .trim()
              .split(/\s+/).length
          );
        } else {
          setError(data.message);
        }
      } catch (err) {
        setError(err.response?.data?.message || "Error fetching document");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [id]);

  // Add effect to update highlighting when findText changes
  useEffect(() => {
    if (!findText.trim() || !isEditing) {
      setHighlightedContent("");
      setMatches([]);
      setCurrentMatchIndex(0);
      setFindCount(0);
    }
  }, [findText, isEditing]);

  useEffect(() => {
    if (replacePendingRef.current) {
      replacePendingRef.current = false;
      highlightMatches();
    }
  }, [editedContent]);

  const handleSave = async () => {
    try {
      const response = await apiService.updateAudioDocument(id, {
        title: editedTitle,
        content: editedContent,
        minutes: editedMinutes,
      });
      const data = response.data;

      if (data.success) {
        setDocument(data.document);
        setTitle(editedTitle);
        setContent(editedContent);
        setMinutes(editedMinutes);
        setIsEditing(false);
        setSaveSuccess(true);
        // Hide success message after 3 seconds
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error updating document");
      console.error(err);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this document?")) {
      try {
        const response = await apiService.deleteAudioDocument(id);
        const data = response.data;

        if (data.success) {
          navigate("/dashboard/teacher");
        } else {
          setError(data.message);
        }
      } catch (err) {
        setError(err.response?.data?.message || "Error deleting document");
        console.error(err);
      }
    }
  };

  // Updated find function to highlight matches
  const highlightMatches = () => {
    if (!findText.trim()) {
      setHighlightedContent("");
      setMatches([]);
      setFindCount(0);
      setCurrentMatchIndex(0);
      return;
    }

    try {
      // Find all matches and their positions
      const regex = new RegExp(escapeRegExp(findText), "gi");
      const foundMatches = [];
      let match;

      // Reset regex lastIndex
      regex.lastIndex = 0;

      while ((match = regex.exec(editedContent)) !== null) {
        foundMatches.push({
          start: match.index,
          end: match.index + match[0].length,
          text: match[0],
        });
      }

      setMatches(foundMatches);
      setFindCount(foundMatches.length);

      if (foundMatches.length > 0) {
        scrollToMatch(foundMatches[0], 0);
      }
    } catch (err) {
      console.error("Error in regex search:", err);
    }
  };

  // Function to scroll to a specific match
  const scrollToMatch = (match, index) => {
    setCurrentMatchIndex(index);

    if (textareaRef.current && match) {
      const textarea = textareaRef.current;
      textarea.focus();
      textarea.setSelectionRange(match.start, match.end);

      // Calculate position to scroll to
      const textBeforeMatch = editedContent.substring(0, match.start);
      const lineHeight = 20; // Approximate line height in pixels
      const lines = textBeforeMatch.split("\n").length;
      const scrollPosition = lines * lineHeight;

      // Scroll to position
      textarea.scrollTop = scrollPosition - textarea.clientHeight / 2;
    }
  };

  // Function to navigate to next match
  const goToNextMatch = () => {
    if (matches.length === 0) return;

    const nextIndex = (currentMatchIndex + 1) % matches.length;
    scrollToMatch(matches[nextIndex], nextIndex);
  };

  // Function to navigate to previous match
  const goToPrevMatch = () => {
    if (matches.length === 0) return;

    const prevIndex = (currentMatchIndex - 1 + matches.length) % matches.length;
    scrollToMatch(matches[prevIndex], prevIndex);
  };

  // Add find function
  const handleFind = () => {
    highlightMatches();
  };

  // Add replace function
  const handleReplace = () => {
    if (!findText.trim() || matches.length === 0) return;

    // Replace current match
    const match = matches[currentMatchIndex];
    const beforeMatch = editedContent.substring(0, match.start);
    const afterMatch = editedContent.substring(match.end);
    const newContent = beforeMatch + replaceText + afterMatch;

    setEditedContent(newContent);

    // Update matches after replace
    // setTimeout(() => highlightMatches(), 0);
    replacePendingRef.current = true;
  };

  // Add replace all function
  const handleReplaceAll = () => {
    if (!findText.trim()) return;

    const regex = new RegExp(escapeRegExp(findText), "gi");
    const newContent = editedContent.replace(regex, replaceText);
    setEditedContent(newContent);

    // Clear matches after replace all
    setMatches([]);
    setFindCount(0);
    setCurrentMatchIndex(0);
  };

  // Helper function to escape special regex characters
  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className="document-view-container">
      <div className="document-header">
        <h2>{isEditing ? "Edit Document" : "Image Document Details"}</h2>
        <div className="document-actions">
          <button onClick={() => navigate("/dashboard/teacher")} className="back-button">
            Back to Dashboard
          </button>
          {!isEditing ? (
            <>
              {/* <button onClick={() => setIsEditing(true)} className="edit-button">
                Edit
              </button> */}
              <button onClick={handleDelete} className="delete-button">
                Delete
              </button>
            </>
          ) : (
            <>
              <button onClick={handleSave} className="save-button">
                Save
              </button>
              <button onClick={() => setShowFindReplace(!showFindReplace)} className="find-replace-toggle-button">
                {showFindReplace ? "Hide Find/Replace" : "Find/Replace"}
              </button>
              <button onClick={() => setIsEditing(false)} className="cancel-button">
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {saveSuccess && <p className="success-message">Document saved successfully!</p>}
      {error && <p className="error-message">{error}</p>}

      {isEditing && showFindReplace && (
        <div className="find-replace-container">
          <div className="find-replace-row">
            <input type="text" value={findText} onChange={(e) => setFindText(e.target.value)} placeholder="Find text..." className="find-input" />
            <button onClick={handleFind} className="find-button">
              Find
            </button>
            <span className="find-count">{findCount > 0 ? `${currentMatchIndex + 1} of ${findCount} matches` : "No matches"}</span>
            {findCount > 0 && (
              <div className="match-navigation">
                <button onClick={goToPrevMatch} className="prev-match-button">
                  ↑ Prev
                </button>
                <button onClick={goToNextMatch} className="next-match-button">
                  ↓ Next
                </button>
              </div>
            )}
          </div>
          <div className="find-replace-row">
            <input type="text" value={replaceText} onChange={(e) => setReplaceText(e.target.value)} placeholder="Replace with..." className="replace-input" />
            <button onClick={handleReplace} className="replace-button">
              Replace
            </button>
            <button onClick={handleReplaceAll} className="replace-all-button">
              Replace All
            </button>
          </div>
        </div>
      )}

      {
        <span>
          <b>Total Worlds:</b> {totalword}
        </span>
      }

      <div className="document-content">
        {!isEditing ? (
          <>
            <h3>{title}</h3>
            <div className="document-meta">
              <p>
                <b>Time Taken:</b> {minutes} minutes
              </p>
            </div>

            <div className="document-image">
              <audio src={image} alt="Document" controls />
            </div>

            <div className="document-text">{content}</div>
          </>
        ) : (
          <>
            <input type="text" value={editedTitle} onChange={(e) => setEditedTitle(e.target.value)} className="title-input" />

            <input type="number" value={editedMinutes} onChange={(e) => setEditedMinutes(e.target.value)} min="1" className="title-input" placeholder="Enter minutes" />

            <textarea
              ref={textareaRef}
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="content-textarea"
              style={{
                caretColor: "black",
                backgroundColor: findCount > 0 ? "#ffffd0" : "white",
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default AudioDocumentView;
